<script setup lang="ts">
  import { computed, ref, onMounted } from 'vue';
  import { MenuSize, type MenuOption } from './Menu.types';
  import { Typo, TypoVariant } from '@/components';

  interface MenuProps {
    options: MenuOption[];
    size?: `${MenuSize}`;
    textKey?: string;
    valueKey?: string;
  }

  interface MenuEmits {
    (e: 'option-click', option: MenuOption): void;
  }

  const props = withDefaults(defineProps<MenuProps>(), {
    size: MenuSize.S,
    textKey: 'label',
    valueKey: 'value',
  });

  const emit = defineEmits<MenuEmits>();

  const optionRefs = ref<(HTMLElement | null)[]>([]);

  const onOptionClick = (option: MenuOption) => {
    if (!option.disabled) {
      emit('option-click', option);
    }
  };

  const typoVariant = computed(() => (props.size === MenuSize.S ? TypoVariant.BodyMRegular : TypoVariant.BodyLRegular));

  const scrollToOption = (index: number) => {
    const optionElement = optionRefs.value[index];
    if (optionElement) {
      optionElement.scrollIntoView({
        block: 'nearest',
        inline: 'start',
      });
    }
  };

  onMounted(() => {
    const index = props.options.findIndex((option) => option.selected);

    if (index !== -1) {
      scrollToOption(index);
    }
  });
</script>

<template>
  <ul data-test-id="menu" :class="['tl-menu', `tl-menu_size-${size}`]" tabindex="-1">
    <li
      v-for="option in options"
      :key="option[valueKey]"
      ref="optionRefs"
      data-test-id="item"
      :class="[
        'tl-menu__item',
        {
          'tl-menu__item_selected': option.selected,
          'tl-menu__item_disabled': option.disabled,
        },
      ]"
      @click.stop="onOptionClick(option)"
    >
      <slot name="option" :option="option">
        <Typo class="tl-menu__item-typo" :variant="typoVariant">{{ option[textKey] }}</Typo>
      </slot>
    </li>
  </ul>
</template>

<style lang="scss" scoped>
  .tl-menu {
    $root: &;

    display: flex;
    flex-direction: column;
    list-style: none;
    margin: 0;
    padding: var(--spacing-xs) 0;

    &_size {
      &-m {
        #{$root}__item {
          height: 40px;
          padding: 0 var(--spacing-m);
        }
      }

      &-l {
        #{$root}__item {
          height: 48px;
          padding: 0 var(--spacing-m);
        }
      }
    }

    &__item {
      align-items: center;
      background-color: var(--color-bg-primary);
      cursor: pointer;
      display: flex;
      flex-shrink: 0;
      height: 32px;
      padding: 0 var(--spacing-s);
      transition: var(--transition-default);

      &_selected {
        background-color: var(--color-neutral-20);
      }

      &_disabled {
        color: var(--color-text-tertiary);
        cursor: default;
      }

      &:hover:not(&_disabled) {
        background-color: var(--color-slate-10);
      }
    }

    &__item-typo {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
</style>
